import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { RootState } from 'store/store';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { stagesActions, stageSelectors } from 'modules/championship/store/stages';

export interface SelectedStageFromRoute {
  stage: StageV2 | null;
  /** Этапы загружены, но такого этапа нет — экран должен увести на список. */
  isMissing: boolean;
  /** Этап найден, и стор уже знает, что выбран именно он. */
  isReady: boolean;
}

/**
 * Делает `:stageId` из адреса источником правды для выбранного этапа.
 *
 * Сам этап читается по id из роута, а не через `selectedStageSelector`, — так он
 * корректен уже на первом рендере. `stages.selectedId` при этом остаётся: на нём
 * завязаны полдесятка селекторов (`selectSelectedStageGroups`, `stageRoundsSelector`,
 * `stageBracketsSelector`…) и два редьюсера, и переписывать их — отдельная задача.
 *
 * `isReady` намеренно требует, чтобы стор уже догнал адрес: иначе на кадре после
 * смены `:stageId` эти селекторы отдали бы данные предыдущего этапа.
 */
export const useSelectedStageFromRoute = (isStagesReady: boolean): SelectedStageFromRoute => {
  const dispatch = useDispatch();
  const { stageId: rawStageId } = useParams();

  const stageId = Number(rawStageId);
  // `/steps/create` тоже подходит под шаблон `:stageId`, поэтому проверяем именно число.
  const isValidId = Boolean(rawStageId) && Number.isInteger(stageId) && stageId > 0;

  const stage = useSelector((state: RootState) => (isValidId ? stageSelectors.selectById(state, stageId) : undefined));
  const selectedId = useSelector((state: RootState) => state.championships.stages.selectedId);

  useEffect(() => {
    if (!isValidId || selectedId === stageId) {
      return;
    }

    dispatch(stagesActions.stageSelected(stageId));
  }, [dispatch, isValidId, selectedId, stageId]);

  // Уход с экрана этапа снимает выбор: иначе селекторы, завязанные на `selectedId`,
  // продолжали бы отдавать данные закрытого этапа (IMGM-1398).
  useEffect(() => {
    return () => {
      dispatch(stagesActions.stageSelected(null));
    };
  }, [dispatch]);

  return {
    stage: stage ?? null,
    isMissing: isStagesReady && (!isValidId || !stage),
    isReady: isStagesReady && Boolean(stage) && selectedId === stageId,
  };
};
